import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DiagnosisField } from '@/components/patient/ReferralDiagnosisField';
import { useReferrerIdentity } from '@/components/patient/useReferrerIdentity';
import { ApiCallError, callFunction } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { diagnoseTextForReferral } from '@/lib/referral/xref01Validation';
import {
  DIAGNOSIS_TEXT_MAX,
  MEDICATION_LIMITS,
  emptyMed,
  pnLast24h,
  stateFromPrefill,
  xdis18Issues,
  xdis18Payload,
  type DateTime,
  type MedRow,
  type Party,
  type Xdis18FormState,
} from '@/lib/xdis/xdis18Form';
import { signedByFromPractitioner, withSignedByFallback } from '@/lib/xdis/xdisSignedBy';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE } from '@/lib/typography';
import { useXdisSender } from './useXdisSender';
import { XdisReceiverChooser } from './XdisReceiverChooser';
import { XDIS18_TESTCENTER_EAN, chosenReceiver, emptyParty, type ReceiverChoice } from '@/lib/xdis/xdisReceiver';
import { XdisViewer } from './XdisViewer';

// Spor A block 3, T3.3 (XDIS-EPJ 7.1–7.9): "Udskrivningsrapport (XDIS18)" from a received
// XDIS16. Prefill asks Communicate for reuse from the latest XDIS21 (7.2) and falls back
// to the XDIS16. The patient identifier and the EpisodeOfCareIdentifier are taken from
// the stored XDIS16 inside Communicate. Clinio has no medication list: depot and PN
// medicine are a manual table prefilled from the source letter, and only PN given within
// 24 hours before discharge is sent (7.6).

const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';
const BOX = 'space-y-2 rounded-md border border-ds-line p-3 ';

type BuildResult = {
  id: string;
  ok: boolean;
  episodeOfCareIdentifier: string | null;
  checks: { name?: string; pass?: boolean; detail?: string }[];
  xml: string;
};

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className={FORM_LABEL_TYPE}>{label}</span>
      {children}
    </label>
  );
}

function DateTimeInput({ label, value, onChange, testId }: { label: string; value: DateTime; onChange: (v: DateTime) => void; testId: string }) {
  return (
    <fieldset className="grid grid-cols-2 gap-2" data-testid={testId}>
      <legend className={FORM_LABEL_TYPE}>{label}</legend>
      <input type="date" className={FIELD} value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} aria-label={`${label} dato`} />
      <input type="time" className={FIELD} value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} aria-label={`${label} klokkeslæt`} />
    </fieldset>
  );
}

function MedTable({ title, rows, onChange, testId }: { title: string; rows: MedRow[]; onChange: (rows: MedRow[]) => void; testId: string }) {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<MedRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className={BOX} data-testid={testId}>
      <div className="text-sm font-medium">{title}</div>
      {rows.length === 0 && <p className={FORM_HINT_TYPE}>{t('patients.xdis.med_none', 'Ingen præparater.')}</p>}
      {rows.map((m, i) => (
        <div key={i} className="grid grid-cols-1 gap-2 border-t border-ds-line pt-2 sm:grid-cols-6">
          <input className={FIELD} placeholder={t('patients.xdis.med_name', 'Præparat')} maxLength={MEDICATION_LIMITS.nameOfDrug} value={m.nameOfDrug} onChange={(e) => set(i, { nameOfDrug: e.target.value })} />
          <input className={FIELD} placeholder={t('patients.xdis.med_form', 'Form')} maxLength={MEDICATION_LIMITS.dosageForm} value={m.dosageForm} onChange={(e) => set(i, { dosageForm: e.target.value })} />
          <input className={FIELD} placeholder={t('patients.xdis.med_strength', 'Styrke')} maxLength={MEDICATION_LIMITS.drugStrength} value={m.drugStrength} onChange={(e) => set(i, { drugStrength: e.target.value })} />
          <input className={FIELD} placeholder={t('patients.xdis.med_route', 'Administrationsvej')} maxLength={MEDICATION_LIMITS.routeOfAdministration} value={m.routeOfAdministration} onChange={(e) => set(i, { routeOfAdministration: e.target.value })} />
          <input type="date" className={FIELD} aria-label={t('patients.xdis.med_latest_date', 'Senest givet, dato')} value={m.latest.date} onChange={(e) => set(i, { latest: { ...m.latest, date: e.target.value } })} />
          <div className="flex gap-2">
            <input type="time" className={FIELD} aria-label={t('patients.xdis.med_latest_time', 'Senest givet, klokkeslæt')} value={m.latest.time} onChange={(e) => set(i, { latest: { ...m.latest, time: e.target.value } })} />
            <Button type="button" size="sm" variant="secondary" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              {t('patients.xdis.remove', 'Fjern')}
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="secondary" onClick={() => onChange([...rows, emptyMed()])}>
        {t('patients.xdis.med_add', 'Tilføj præparat')}
      </Button>
    </div>
  );
}

function TriState({ label, value, onChange }: { label: string; value: boolean | null; onChange: (v: boolean | null) => void }) {
  const { t } = useTranslation();
  return (
    <Labelled label={label}>
      <select className={FIELD} value={value === null ? '' : value ? 'true' : 'false'} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}>
        <option value="">{t('patients.xdis.not_stated', 'Ikke angivet')}</option>
        <option value="true">{t('patients.xdis.yes', 'Ja')}</option>
        <option value="false">{t('patients.xdis.no', 'Nej')}</option>
      </select>
    </Labelled>
  );
}

export function Xdis18Form({
  clinicId,
  patientId,
  patientName,
  sourceExchangeId,
  canRevealCpr = true,
  onClose,
}: {
  clinicId: string;
  patientId: string;
  patientName: string | null;
  sourceExchangeId: string;
  canRevealCpr?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const referrer = useReferrerIdentity(user?.id, clinicId);
  const { sender, gap } = useXdisSender(clinicId);
  const [form, setForm] = useState<Xdis18FormState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<BuildResult | null>(null);
  // XDIS-EPJ 7.8: the receiver defaults to the XDIS16's sender and can be changed.
  const [defaultReceiver, setDefaultReceiver] = useState<Party | null>(null);
  const [receiverChoice, setReceiverChoice] = useState<ReceiverChoice>({ mode: 'default', party: null });

  useEffect(() => {
    let cancelled = false;
    callFunction<Record<string, unknown>, Record<string, unknown>>('communicate-xdis', {
      action: 'prefill18',
      clinic_id: clinicId,
      source_exchange_id: sourceExchangeId,
      reuse_source: 'latest-xdis21',
    }).then(
      (data) => {
        if (cancelled) return;
        const raw = { ...(data.prefill as Record<string, unknown> | undefined), ...data } as Record<string, unknown>;
        const state = stateFromPrefill(raw);
        setDefaultReceiver(state.receiver);
        setForm(state);
      },
      (err: unknown) => !cancelled && setLoadError(err instanceof ApiCallError ? err.message : t('patients.xdis.prefill_failed', 'Indlæggelsesrapporten kunne ikke læses.')),
    );
    return () => {
      cancelled = true;
    };
  }, [clinicId, sourceExchangeId, t]);

  const formReady = form !== null;
  useEffect(() => {
    if (!referrer.data) return;
    const fallback = signedByFromPractitioner(referrer.data.name, referrer.data.title);
    setForm((current) => (current ? withSignedByFallback(current, fallback) : current));
  }, [formReady, referrer.data]);

  if (loadError) return <p role="alert" className={FORM_ERROR_TYPE}>{loadError}</p>;
  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-ds-muted" aria-label={t('patients.xdis.loading', 'Henter…')} />;

  const update = (patch: Partial<Xdis18FormState>) => setForm({ ...form, ...patch });
  const issues = xdis18Issues(form);
  const pnSent = pnLast24h(form.pn, form.discharge);

  async function onSubmit() {
    if (!form) return;
    setAttempted(true);
    if (issues.length > 0) {
      toast.error(t('patients.xdis.form_invalid', 'Rapporten er ikke dannet. Ret de markerede felter.'));
      return;
    }
    if (!sender) {
      toast.error(gap ?? t('patients.xdis.sender_loading', 'Klinikkens oplysninger er ikke indlæst endnu.'));
      return;
    }
    setPending(true);
    try {
      const built = await callFunction<Record<string, unknown>, BuildResult>('communicate-xdis', {
        action: 'build18',
        clinic_id: clinicId,
        patient_id: patientId,
        source_exchange_id: sourceExchangeId,
        report: xdis18Payload(form, sender),
      });
      setResult(built);
      await queryClient.invalidateQueries({ queryKey: ['hospital-referral-outbox', clinicId, patientId] });
      await queryClient.invalidateQueries({ queryKey: ['communicate-xdis-flow', clinicId, patientId] });
      if (built.ok) toast.success(t('patients.xdis.built18', 'Udskrivningsrapporten er dannet og ligger i udbakken.'));
      else toast.error(t('patients.xdis.built18_failed_checks', 'Udskrivningsrapporten bestod ikke kontrollen og er ikke lagt i udbakken.'));
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('patients.xdis.build18_failed', 'Udskrivningsrapporten kunne ikke dannes.'));
    } finally {
      setPending(false);
    }
  }

  const err = (field: string) =>
    attempted
      ? issues.filter((i) => i.field === field).map((i) => (
          <p key={i.message} role="alert" className={FORM_ERROR_TYPE}>
            {i.message}
          </p>
        ))
      : null;

  return (
    <div className="space-y-4" data-testid="xdis18-form">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-base font-semibold">{t('patients.xdis.form18_title', 'Udskrivningsrapport (XDIS18)')}</h4>
        <Button type="button" size="sm" variant="secondary" onClick={onClose}>
          {t('patients.xdis.close', 'Luk')}
        </Button>
      </div>
      <p className={FORM_HINT_TYPE}>
        {t(
          'patients.xdis.form18_hint',
          'Udfyldt fra indlæggelsesrapporten. Patientens identifikation og forløbsnummer (EpisodeOfCareIdentifier) hentes fra den modtagne rapport og kan ikke ændres her.',
        )}
      </p>
      <p className={FORM_HINT_TYPE} data-testid="xdis18-reuse-source">
        {form.reuseSource === 'xdis21'
          ? t('patients.xdis.form18_reuse21', 'Data er genbrugt fra den seneste plejeforløbsplan (XDIS21).')
          : t('patients.xdis.form18_reuse16', 'Data er genbrugt fra indlæggelsesrapporten (XDIS16). Ingen plejeforløbsplan fundet.')}
      </p>
      {gap && <p role="alert" className={FORM_ERROR_TYPE}>{gap}</p>}

      <div className={BOX} data-testid="xdis18-receiver">
        <XdisReceiverChooser
          fieldId="xdis18-receiver"
          choice={receiverChoice}
          onChange={(choice) => {
            setReceiverChoice(choice);
            update({ receiver: chosenReceiver(choice, defaultReceiver) ?? emptyParty() });
          }}
          defaultLabel={t('patients.xdis.receiver_default18', 'Indlæggelsesrapportens afsender')}
          defaultParty={defaultReceiver}
          testcenterEan={XDIS18_TESTCENTER_EAN}
          showErrors={attempted}
        />
        {err('receiver')}
      </div>

      <div className={BOX}>
        <span className="text-sm font-medium">{t('patients.xdis.signed_by', 'Underskrevet af')}</span>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Labelled label={t('patients.xdis.given_name', 'Fornavn')}>
            <input className={FIELD} maxLength={70} value={form.signedBy.givenName} onChange={(e) => update({ signedBy: { ...form.signedBy, givenName: e.target.value } })} />
          </Labelled>
          <Labelled label={t('patients.xdis.surname', 'Efternavn')}>
            <input className={FIELD} maxLength={70} value={form.signedBy.surname} onChange={(e) => update({ signedBy: { ...form.signedBy, surname: e.target.value } })} />
          </Labelled>
          <Labelled label={t('patients.xdis.title', 'Titel')}>
            <input className={FIELD} maxLength={25} value={form.signedBy.title} onChange={(e) => update({ signedBy: { ...form.signedBy, title: e.target.value } })} />
          </Labelled>
        </div>
        {err('signedBy')}
      </div>

      <div className={`${BOX} grid grid-cols-1 gap-3 sm:grid-cols-3`}>
        <div>
          <DateTimeInput label={t('patients.xdis.admission', 'Indlagt')} value={form.admission} onChange={(v) => update({ admission: v })} testId="xdis18-admission" />
          {err('admission')}
        </div>
        <div>
          <DateTimeInput label={t('patients.xdis.end_of_treatment', 'Færdigbehandlet')} value={form.endOfTreatment} onChange={(v) => update({ endOfTreatment: v })} testId="xdis18-end-of-treatment" />
          {err('endOfTreatment')}
        </div>
        <div>
          <DateTimeInput label={t('patients.xdis.discharge', 'Udskrevet')} value={form.discharge} onChange={(v) => update({ discharge: v })} testId="xdis18-discharge" />
          {err('discharge')}
        </div>
      </div>

      <div className={BOX}>
        <Labelled label={t('patients.xdis.cause', 'Indlæggelsesårsag')}>
          <textarea className={FIELD} rows={2} value={form.causeOfAdmission} onChange={(e) => update({ causeOfAdmission: e.target.value })} />
        </Labelled>
        {err('causeOfAdmission')}
        <Labelled label={t('patients.xdis.course', 'Indlæggelsesforløb')}>
          <textarea className={FIELD} rows={3} value={form.courseOfAdmission} onChange={(e) => update({ courseOfAdmission: e.target.value })} />
        </Labelled>
        <Labelled label={t('patients.xdis.contagion', 'Smitterisiko')}>
          <textarea className={FIELD} rows={2} value={form.riskOfContagion} onChange={(e) => update({ riskOfContagion: e.target.value })} />
        </Labelled>
        <Labelled label={t('patients.xdis.cave', 'CAVE')}>
          <textarea className={FIELD} rows={2} value={form.cave} onChange={(e) => update({ cave: e.target.value })} />
        </Labelled>
      </div>

      <div className={BOX} data-testid="xdis18-diagnoses">
        <span className="text-sm font-medium">{t('patients.xdis.diagnoses', 'Diagnoser (SKS)')}</span>
        <ul className="space-y-1">
          {form.diagnoses.map((d, i) => (
            <li key={`${d.code}-${i}`} className="flex items-center justify-between gap-2 text-sm">
              <span>
                <span className="font-mono">{d.code}</span> {d.text}
              </span>
              <Button type="button" size="sm" variant="secondary" onClick={() => update({ diagnoses: form.diagnoses.filter((_, j) => j !== i) })}>
                {t('patients.xdis.remove', 'Fjern')}
              </Button>
            </li>
          ))}
        </ul>
        <DiagnosisField
          key={`xdis18-diagnosis-${form.diagnoses.length}`}
          inputId="xdis18-diagnosis"
          label={t('patients.xdis.diagnosis_add', 'Tilføj diagnose')}
          selected={null}
          onSelect={(d) => update({ diagnoses: [...form.diagnoses, { code: d.code, text: diagnoseTextForReferral(d.textDa).slice(0, DIAGNOSIS_TEXT_MAX) }] })}
          onClear={() => undefined}
        />
        {err('diagnoses')}
      </div>

      <div className="space-y-2">
        <p className={FORM_HINT_TYPE}>
          {t('patients.xdis.med_hint', 'Clinio har ingen medicinliste. Præparaterne er udfyldt fra indlæggelsesrapporten og kan rettes her.')}
        </p>
        <MedTable title={t('patients.xdis.depot', 'Depotmedicin (senest givet)')} rows={form.depot} onChange={(rows) => update({ depot: rows })} testId="xdis18-depot" />
        {err('depot')}
        <MedTable title={t('patients.xdis.pn', 'PN-medicin')} rows={form.pn} onChange={(rows) => update({ pn: rows })} testId="xdis18-pn" />
        <p className={FORM_HINT_TYPE} data-testid="xdis18-pn-count">
          {t('patients.xdis.pn_24h', 'Kun PN givet inden for 24 timer før udskrivning sendes: {{count}} af {{total}}.', {
            count: pnSent.length,
            total: form.pn.length,
          })}
        </p>
        {err('pn')}
        <Labelled label={t('patients.xdis.medicine_comment', 'Kommentar til medicin')}>
          <textarea className={FIELD} rows={2} value={form.medicineComment} onChange={(e) => update({ medicineComment: e.target.value })} />
        </Labelled>
      </div>

      <div className={`${BOX} grid grid-cols-1 gap-2 sm:grid-cols-2`} data-testid="xdis18-discharge-medicine">
        <span className="text-sm font-medium sm:col-span-2">{t('patients.xdis.discharge_medicine', 'Medsendt medicin')}</span>
        <Labelled label={t('patients.xdis.attached_expire', 'Medsendt medicin rækker til')}>
          <input type="date" className={FIELD} value={form.attachedExpire} onChange={(e) => update({ attachedExpire: e.target.value })} />
        </Labelled>
        <Labelled label={t('patients.xdis.attached_comment', 'Kommentar')}>
          <input className={FIELD} value={form.attachedComment} onChange={(e) => update({ attachedComment: e.target.value })} />
        </Labelled>
        <TriState label={t('patients.xdis.receipt', 'Recept udstedt')} value={form.receipt} onChange={(v) => update({ receipt: v })} />
        <TriState label={t('patients.xdis.pickup', 'Afhentning/levering aftalt')} value={form.pickUpOrDelivery} onChange={(v) => update({ pickUpOrDelivery: v })} />
        <TriState label={t('patients.xdis.dosage_reordered', 'Dosisdispensering genbestilt')} value={form.dosageExemptionReordered} onChange={(v) => update({ dosageExemptionReordered: v })} />
      </div>

      <Labelled label={t('patients.xdis.future_plans', 'Fremtidige planer')}>
        <textarea className={FIELD} rows={3} value={form.futurePlans} onChange={(e) => update({ futurePlans: e.target.value })} />
      </Labelled>

      <div className="flex gap-2">
        <Button type="button" onClick={() => void onSubmit()} disabled={pending || Boolean(gap)} data-testid="xdis18-submit">
          {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          {t('patients.xdis.build18', 'Dan udskrivningsrapport')}
        </Button>
      </div>

      {result && (
        <div className="space-y-2" data-testid="xdis18-result">
          <ul className="space-y-1 text-sm">
            {result.checks.map((c, i) => (
              <li key={i} className={c.pass === false ? 'text-ds-red-ink' : 'text-ds-green-ink '}>
                {c.pass === false ? '✗' : '✓'} {c.name} {c.detail ? `— ${c.detail}` : ''}
              </li>
            ))}
          </ul>
          {result.xml && <XdisViewer xml={result.xml} kind="XDIS18" patientId={patientId} patientName={patientName} canRevealCpr={canRevealCpr} />}
        </div>
      )}
    </div>
  );
}
